import React, {ReactElement, ReactNode, useCallback} from 'react'
import {FormSection} from "../../../common/components/SectionedForm";
import {api} from "../../../common/api";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import PaperStack from "../../../common/form/PaperStack";
import FormGroup from "../../../common/form/FormGroup";
import {LabelValues, LabelValueType} from "../../../common/components/LabelValues";
import {CallQueueRouting} from "../../../app/types";
import {useFormSubmissionButtons} from "../../../common/form/useFormSubmissmionButtons";
import {Route} from "@mui/icons-material";
import {meSelector} from "../../../authentication/redux";
import {useFetchData} from "../../../common/servercall/useFetchData";
import {CallQueueRoutingForm} from "./callQueueRoutingForm";
import {Grid, Paper, Table, TableContainer} from "@mui/material";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";

export const useCallQueueRoutingSection = () : FormSection<CallQueueRouting> => {
    const { t } = useTranslation()
    const me = useSelector(meSelector)
    const {buildFormButtons} = useFormSubmissionButtons(false)

    const fetchData = useCallback(
        async (): Promise<CallQueueRouting | undefined> => {
            if (!me) return
            const { data } = await api.get<CallQueueRouting>(`/api/settings/${me.voiceCloudProductId}/call-queue-routing-settings`)
            return data
        },
        [me]
    )
    const { data, isFetching, error, refetch } = useFetchData<CallQueueRouting>(useCallback(() => fetchData(), [fetchData]))

    const onSubmit = useCallback((onSucceeded: () => void) =>
        async (formData: CallQueueRouting) => {
            if (!me) return
            await api.put(`/api/settings/${me.voiceCloudProductId}/call-queue-routing-settings`, formData)
            await refetch()
            onSucceeded();
        },
        [me, refetch]
    )

    const renderGeneralForm = useCallback(
        (callQueueRouting: CallQueueRouting, cancelEditButton: ReactElement, onSucceeded: () => void): ReactNode =>
            <CallQueueRoutingForm callQueueRouting={callQueueRouting} onSubmit={onSubmit(onSucceeded)} buildFormButtons={buildFormButtons(cancelEditButton)}/>
        , [onSubmit, buildFormButtons])

    const infoLabelValues = useCallback(
        (value: CallQueueRouting): LabelValueType[] => [
            { label: t('settings.call-queue-routing.policy'), value: <>{t(`settings.call-queue-routing.policies.${value.routingPolicy}`)}</> },
            // @ts-ignore
            ...(value.routingPolicy === 'PRIORITY' ? [{
                label: '',
                type: 'FULL_LINE',
                value: (
                    <Grid container>
                        <Grid item xs={5}>
                            <TableContainer component={Paper}>
                                <Table sx={{minWidth: 300}} size={'small'}>
                                    <TableHead>
                                        <TableCell style={{ fontWeight: 700 }} >
                                            {t('flows.table.name')}
                                        </TableCell>
                                        <TableCell style={{ fontWeight: 700 }} align="left">
                                            Prio
                                        </TableCell>
                                    </TableHead>
                                    <TableBody>
                                        {value.queuePriorities.map((it, index) =>
                                            <TableRow key={it.businessId}
                                                      sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                                                <TableCell  scope="row">
                                                    {it.name}
                                                </TableCell>
                                                <TableCell scope="row" align="left">
                                                    {index + 1}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>)
            }] : []),
        ],
        [t]
    )

    const renderDetails = useCallback(
        (callQueueRouting: CallQueueRouting): ReactNode => {
            return (
                <PaperStack>
                    <FormGroup fullWidth={true} key={1} label={''}>
                        <LabelValues labelValues={infoLabelValues(callQueueRouting)} />
                    </FormGroup>
                    <></>
                </PaperStack>
            )
        },
        [infoLabelValues]
    )

    return {
        title: t('settings.call-queue-routing.title'),
        data: data,
        isFetching: isFetching,
        error: error,
        refetch: refetch,
        renderDetails: renderDetails,
        renderForm: renderGeneralForm,
        enabled: () => true,
        icon: () => <Route/>
    }
}